import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { getRequestHeader } from "@tanstack/react-start/server";
import { assertRateLimit } from "@/lib/rate-limit.server";

function callerIp(): string {
  return (
    getRequestHeader("cf-connecting-ip") ??
    getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ??
    "unknown"
  );
}

const SUPA_URL = process.env.PLUGGZ_SUPABASE_URL ?? "https://kzdhvepwoytrhyjlriju.supabase.co";
const SUPA_ANON = process.env.PLUGGZ_SUPABASE_ANON_KEY ?? "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imt6ZGh2ZXB3b3l0cmh5amxyaWp1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODEwMTc1MjEsImV4cCI6MjA5NjU5MzUyMX0.JZiBIXiixTKF8VuviLSEMSjqNuEQGMuJeRmBHQ0xuc8";

function serverSupabase() {
  return createClient(SUPA_URL, SUPA_ANON, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
}

async function verifyToken(token: string | null, action: string): Promise<{ ok: boolean; error?: string }> {
  const secret = process.env.RECAPTCHA_SECRET_KEY;
  if (!secret) return { ok: true }; // not configured — skip silently
  if (!token) {
    console.error("[recaptcha] Secret key is configured but no token was received from client — check that VITE_RECAPTCHA_SITE_KEY is set at BUILD TIME in Cloudflare Pages, not just as a runtime secret");
    return { ok: false, error: "captcha_required" };
  }
  const body = new URLSearchParams({ secret, response: token });
  const res = await fetch("https://www.google.com/recaptcha/api/siteverify", {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  const json = (await res.json()) as { success?: boolean; score?: number; action?: string };
  const ok = !!json.success && (json.score ?? 0) >= 0.5 && (json.action === action || !action);
  return ok ? { ok: true } : { ok: false, error: "captcha_failed" };
}

export type VerifiedSignInResult =
  | { ok: true; session: { access_token: string; refresh_token: string }; userId: string }
  | { ok: false; error: string };

/** Verify reCAPTCHA, then sign in with Supabase. Returns session tokens for the client to apply. */
export const verifiedSignIn = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string; password: string; recaptchaToken: string | null }) => data)
  .handler(async ({ data }): Promise<VerifiedSignInResult> => {
    // 10 attempts / 5 min per IP, layered on top of the existing reCAPTCHA check.
    await assertRateLimit(`login:${callerIp()}`, 10, 300);
    const verdict = await verifyToken(data.recaptchaToken, "login");
    if (!verdict.ok) return { ok: false, error: "Could not verify request. Please try again." };

    const supabase = serverSupabase();
    const { data: result, error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });
    if (error || !result.session) return { ok: false, error: error?.message ?? "Sign-in failed" };
    return {
      ok: true,
      userId: result.user!.id,
      session: {
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      },
    };
  });

export type VerifiedSignUpResult =
  | {
      ok: true;
      needsConfirmation: boolean;
      userId: string | null;
      session: { access_token: string; refresh_token: string } | null;
    }
  | { ok: false; error: string };

/** Verify reCAPTCHA, then sign up with Supabase. */
export const verifiedSignUp = createServerFn({ method: "POST" })
  .inputValidator(
    (data: {
      email: string;
      password: string;
      displayName?: string;
      emailRedirectTo?: string;
      recaptchaToken: string | null;
    }) => data,
  )
  .handler(async ({ data }): Promise<VerifiedSignUpResult> => {
    await assertRateLimit(`signup:${callerIp()}`, 10, 300);
    const verdict = await verifyToken(data.recaptchaToken, "signup");
    if (!verdict.ok) return { ok: false, error: "Could not verify request. Please try again." };

    const supabase = serverSupabase();
    const { data: result, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        emailRedirectTo: data.emailRedirectTo,
        data: data.displayName ? { display_name: data.displayName } : undefined,
      },
    });
    if (error) return { ok: false, error: error.message };
    return {
      ok: true,
      needsConfirmation: !result.session,
      userId: result.user?.id ?? null,
      session: result.session
        ? { access_token: result.session.access_token, refresh_token: result.session.refresh_token }
        : null,
    };
  });

/** Check whether a creator application with the same handle or email already exists. */
export const checkDuplicateApplication = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { handle: string }) => data)
  .handler(async ({ data, context }): Promise<{ ok: true } | { ok: false; error: string }> => {
    const { supabase, userId } = context;
    const { data: byHandle } = await supabase
      .from("creators")
      .select("id")
      .eq("handle", data.handle)
      .maybeSingle();
    if (byHandle) return { ok: false, error: "An application with this email or handle already exists. If you need help, contact support." };

    const { data: byUser } = await supabase
      .from("creators")
      .select("id")
      .eq("user_id", userId)
      .maybeSingle();
    if (byUser) return { ok: false, error: "An application with this email or handle already exists. If you need help, contact support." };

    return { ok: true };
  });

export type ApplicationStatusResult =
  | {
      ok: true;
      found: true;
      status: "pending" | "approved" | "rejected";
      created_at: string;
      updated_at: string;
    }
  | { ok: true; found: false }
  | { ok: false; error: string };

const emailSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
});

/**
 * Public lookup: given an email, return the most recent creator-application
 * status. Uses the admin client to resolve email → user_id without leaking
 * any PII beyond the status timestamps.
 */
export const checkApplicationStatus = createServerFn({ method: "POST" })
  .inputValidator((data: { email: string }) => emailSchema.parse(data))
  .handler(async ({ data }): Promise<ApplicationStatusResult> => {
    try {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      // Find the auth user with this email (paginate just the first page;
      // duplicates by email are not allowed in Supabase Auth).
      const { data: list, error: listErr } = await supabaseAdmin.auth.admin.listUsers({
        page: 1,
        perPage: 200,
      });
      if (listErr) return { ok: false, error: "Lookup failed. Please try again." };
      const user = list.users.find((u) => (u.email ?? "").toLowerCase() === data.email);
      if (!user) return { ok: true, found: false };

      const { data: row, error } = await supabaseAdmin
        .from("creators")
        .select("status, created_at, updated_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) return { ok: false, error: "Lookup failed. Please try again." };
      if (!row) return { ok: true, found: false };

      // Treat "live" as approved for the public status view.
      const raw = row.status as "pending" | "approved" | "live" | "rejected";
      const status: "pending" | "approved" | "rejected" =
        raw === "live" ? "approved" : raw;
      return {
        ok: true,
        found: true,
        status,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
      };
    } catch {
      return { ok: false, error: "Lookup failed. Please try again." };
    }
  });